import React, { useRef, useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import CheckBoxOutlineBlankOutlined from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlined from '@mui/icons-material/CheckBoxOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import Edit from '@mui/icons-material/Edit';
import MoreVert from '@mui/icons-material/MoreVert';
import { ActionPlanTaskStatus } from '../../../enums/ActionPlans';
import { useI18n } from '../../../lib/hook/useI18n';

interface Props {
  completeable?: boolean;
  incompleteable?: boolean;
  onChangeStatus: (status: ActionPlanTaskStatus) => void;
  onDelete: () => void;
  onEdit: () => void;
}

const TaskActionsMenu: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useI18n();

  const {
    completeable = false,
    incompleteable = false,
    onChangeStatus,
    onDelete,
    onEdit,
  } = props;

  const [open, setOpen] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggleOpen = () => {
    setOpen(!open);
  };

  const onMarkAsComplete = () => {
    onChangeStatus(ActionPlanTaskStatus.Completed);
  };

  const onMarkAsIncomplete = () => {
    onChangeStatus(ActionPlanTaskStatus.Pending);
  };

  const onAction =
    (
      action:
        | typeof onDelete
        | typeof onEdit
        | typeof onMarkAsComplete
        | typeof onMarkAsIncomplete
    ) =>
    () => {
      action();
      setOpen(false);
    };

  return (
    <>
      <IconButton edge="end" onClick={toggleOpen} ref={buttonRef} size="large">
        <MoreVert />
      </IconButton>

      <Menu
        anchorEl={buttonRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        disablePortal
        onClose={toggleOpen}
        open={open}
      >
        {completeable && (
          <MenuItem dense onClick={onAction(onMarkAsComplete)}>
            <ListItemIcon>
              <CheckBoxOutlined />
            </ListItemIcon>
            <ListItemText primary={t('map.task.details.mark.complete')} />
          </MenuItem>
        )}
        {incompleteable && (
          <MenuItem dense onClick={onAction(onMarkAsIncomplete)}>
            <ListItemIcon>
              <CheckBoxOutlineBlankOutlined />
            </ListItemIcon>
            <ListItemText primary={t('map.task.details.mark.incomplete')} />
          </MenuItem>
        )}
        <MenuItem dense onClick={onAction(onEdit)}>
          <ListItemIcon>
            <Edit />
          </ListItemIcon>
          <ListItemText primary={t('map.task.details.edit')} />
        </MenuItem>
        <Box py={1}>
          <Divider />
        </Box>
        <MenuItem dense onClick={onAction(onDelete)}>
          <ListItemIcon>
            <DeleteOutlined color="error" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography color="error">
                {t('map.task.details.delete')}
              </Typography>
            }
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default TaskActionsMenu;
